import { useCallback, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface AudiobookSession {
  id: string;
  user_id: string;
  book_id: number;
  current_page: number;
  total_listen_time_seconds: number;
  last_listened_at: string;
}

interface BookPlayCount {
  book_id: number;
  play_count: number;
  unique_listeners: number;
  total_listen_time_seconds: number;
}

/**
 * Hook for tracking audiobook listening analytics.
 * Tracks: current page, total listen time, play counts.
 * Syncs with reading_sessions for book reader progress.
 */
export function useAudiobookTracking(bookId: number | null) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const listenStartRef = useRef<number | null>(null);
  const currentPageRef = useRef<number>(1);

  // Fetch user's audiobook session for this book
  const { data: session } = useQuery({
    queryKey: ['audiobook-session', user?.id, bookId],
    queryFn: async () => {
      if (!user || !bookId) return null;
      const { data, error } = await supabase
        .from('audiobook_sessions')
        .select('*')
        .eq('user_id', user.id)
        .eq('book_id', bookId)
        .maybeSingle();
      if (error) throw error;
      return data as AudiobookSession | null;
    },
    enabled: !!user && !!bookId,
    staleTime: 30 * 1000, // 30 seconds
  });

  // Fetch book play counts
  const { data: playCounts } = useQuery({
    queryKey: ['book-play-counts', bookId],
    queryFn: async () => {
      if (!bookId) return null;
      const { data, error } = await supabase
        .from('book_play_counts')
        .select('*')
        .eq('book_id', bookId)
        .maybeSingle();
      if (error && error.code !== 'PGRST116') throw error;
      return data as BookPlayCount | null;
    },
    enabled: !!bookId,
    staleTime: 60 * 1000, // 1 minute
  });

  // Fetch user's reading progress from reading_sessions (for cross-ref)
  const { data: readingProgress } = useQuery({
    queryKey: ['reading-progress', user?.id, bookId],
    queryFn: async () => {
      if (!user || !bookId) return null;
      const { data, error } = await supabase
        .from('reading_sessions')
        .select('current_page')
        .eq('user_id', user.id)
        .eq('book_id', bookId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error && error.code !== 'PGRST116') throw error;
      return data?.current_page || null;
    },
    enabled: !!user && !!bookId,
    staleTime: 30 * 1000,
  });

  // Mutation to update session
  const updateSessionMutation = useMutation({
    mutationFn: async ({ 
      page, 
      listenSeconds 
    }: { 
      page: number; 
      listenSeconds: number;
    }) => {
      if (!user || !bookId) return;
      
      // Upsert audiobook session
      const { error } = await supabase
        .from('audiobook_sessions')
        .upsert({
          user_id: user.id,
          book_id: bookId,
          current_page: page,
          total_listen_time_seconds: (session?.total_listen_time_seconds || 0) + listenSeconds,
          last_listened_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,book_id'
        });
      
      if (error) throw error;

      // Also update book play counts
      await supabase
        .from('book_play_counts')
        .upsert({
          book_id: bookId,
          play_count: (playCounts?.play_count || 0) + 1,
          total_listen_time_seconds: (playCounts?.total_listen_time_seconds || 0) + listenSeconds,
        }, {
          onConflict: 'book_id'
        });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['audiobook-session', user?.id, bookId] });
      queryClient.invalidateQueries({ queryKey: ['book-play-counts', bookId] });
    },
  });

  // Start tracking listening time
  const startListening = useCallback((page: number) => {
    listenStartRef.current = Date.now();
    currentPageRef.current = page;
  }, []);

  // Stop tracking and save progress
  const stopListening = useCallback(() => {
    if (!listenStartRef.current || !user || !bookId) return;
    
    const listenSeconds = Math.floor((Date.now() - listenStartRef.current) / 1000);
    if (listenSeconds > 0) {
      updateSessionMutation.mutate({
        page: currentPageRef.current,
        listenSeconds,
      });
    }
    listenStartRef.current = null;
  }, [user, bookId, updateSessionMutation]);

  // Update page without stopping timer
  const updatePage = useCallback((page: number) => {
    currentPageRef.current = page;
    
    // Save intermediate progress every page change
    if (listenStartRef.current && user && bookId) {
      const listenSeconds = Math.floor((Date.now() - listenStartRef.current) / 1000);
      if (listenSeconds > 5) { // Only save if listened for at least 5 seconds
        updateSessionMutation.mutate({
          page,
          listenSeconds,
        });
        listenStartRef.current = Date.now(); // Reset timer
      }
    }
  }, [user, bookId, updateSessionMutation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (listenStartRef.current) {
        stopListening();
      }
    };
  }, [stopListening]);

  return {
    // Session data
    currentListeningPage: session?.current_page || 1,
    totalListenTimeSeconds: session?.total_listen_time_seconds || 0,
    lastListenedAt: session?.last_listened_at || null,
    
    // Play counts
    playCount: playCounts?.play_count || 0,
    
    // Reading progress (for cross-reference with book reader)
    bookReaderPage: readingProgress,
    
    // Actions
    startListening,
    stopListening,
    updatePage,
    
    // Computed
    hasListeningProgress: !!session,
    totalListenTimeMinutes: Math.floor((session?.total_listen_time_seconds || 0) / 60),
  };
}

/**
 * Hook for fetching all audiobook sessions for a user (for "Continue Listening" feature)
 */
export function useAllAudiobookSessions() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['all-audiobook-sessions', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('audiobook_sessions')
        .select('*, books:book_id(id, title, cover_image_url)')
        .eq('user_id', user.id)
        .order('last_listened_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to manage liked audiobooks
 */
export function useLikedAudiobooks() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: likedBookIds = [] } = useQuery({
    queryKey: ['liked-audiobooks', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('liked_audiobooks')
        .select('book_id')
        .eq('user_id', user.id);
      if (error) throw error;
      return data.map(lb => lb.book_id);
    },
    enabled: !!user,
  });

  const likeMutation = useMutation({
    mutationFn: async (bookId: number) => {
      if (!user) return;
      const { error } = await supabase
        .from('liked_audiobooks')
        .insert({ user_id: user.id, book_id: bookId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['liked-audiobooks', user?.id] });
    },
  });

  const unlikeMutation = useMutation({
    mutationFn: async (bookId: number) => {
      if (!user) return;
      const { error } = await supabase
        .from('liked_audiobooks')
        .delete()
        .eq('user_id', user.id)
        .eq('book_id', bookId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['liked-audiobooks', user?.id] });
    },
  });

  const toggleLike = useCallback((bookId: number) => {
    if (!user) return;
    if (likedBookIds.includes(bookId)) {
      unlikeMutation.mutate(bookId);
    } else {
      likeMutation.mutate(bookId);
    }
  }, [user, likedBookIds, likeMutation, unlikeMutation]);

  return {
    likedBookIds,
    isLiked: (bookId: number) => likedBookIds.includes(bookId),
    toggleLike,
    isLoading: likeMutation.isPending || unlikeMutation.isPending,
  };
}
